import TrackCard from "@/components/ui/TrackCard";
import { useTracksInfiniteList } from "@/lib/lutely-api/hooks/useTracksInfiniteList";
import { marketplaceTokens } from "@/lib/tokens";
import { Flex, Heading } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";
import Slider, { Settings } from "react-slick";
import CarouselShell from "./CarouselShell";
import { useResponsiveSlidesToShow } from "./useResponsiveSlidesToShow";
import { useSliderSettings } from "./useSliderSettings";

type TrackSectionProps = {
  trackType: "songs" | "beats" | "soundkits";
  query: ReturnType<typeof useTracksInfiniteList>;
  title: string;
  hideArrows?: boolean;
};

const titleMap: Record<string, string> = {
  songs: "Songs",
  beats: "Beats",
  soundkits: "Sound Kits",
};

export default function TrackSliderSection({
  trackType,
  query,
  hideArrows,
}: TrackSectionProps) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const tracks = data?.pages.flatMap((page) => page.content) || [];
  const pageSize = data?.pages[0]?.content.length ?? 20;
  const numTracks = tracks.length;

  const sliderRef = useRef<Slider | null>(null);
  const [carouselEl, setCarouselEl] = useState<HTMLDivElement | null>(null);
  const attachCarouselRef = useCallback((node: HTMLDivElement | null) => {
    setCarouselEl(node);
  }, []);
  const title = titleMap[trackType] || "Tracks";
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(7);
  const visibleSlides = useResponsiveSlidesToShow(carouselEl);
  const showRightFade =
    !hideArrows &&
    numTracks > visibleSlides &&
    currentSlide + visibleSlides < numTracks;

  useEffect(() => {
    if (currentSlide + itemsPerSlide >= numTracks - pageSize) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  }, [
    currentSlide,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    itemsPerSlide,
    numTracks,
    pageSize,
  ]);

  const settings: Settings = useSliderSettings({
    slidesToShow: visibleSlides,
    beforeChange: (current: number, next: number) => {
      setItemsPerSlide(Math.abs(next - current));
    },
    afterChange: (current: number) => setCurrentSlide(current),
    arrows: !hideArrows,
    responsive: [],
  });

  if (tracks.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" className="pt-[40px]">
      <Flex className="mb-[15px] items-center">
        <Heading as="h2" className={marketplaceTokens.sectionHeader}>
          {title}
        </Heading>
      </Flex>
      <CarouselShell ref={attachCarouselRef} showRightFade={showRightFade}>
        <Slider
          ref={sliderRef}
          {...settings}
          className={hideArrows ? "mobile-hide-arrows" : ""}
        >
          {tracks.map((track) => (
            <TrackCard key={track.trackId} track={track} />
          ))}
        </Slider>
      </CarouselShell>
    </Flex>
  );
}
